import React, { useState, useRef, useEffect } from 'react';
import { Copy, Check, Sparkles, Download, Layers, LayoutList, FileText } from 'lucide-react';
import { GeneratedResult } from '../types';

interface PromptResultProps {
  results: GeneratedResult[];
  isProcessing: boolean;
  progress: { current: number; total: number };
}

export const PromptResult: React.FC<PromptResultProps> = ({ results, isProcessing, progress }) => {
  const [viewMode, setViewMode] = useState<'batch' | 'individual'>('batch');
  const [globalCopied, setGlobalCopied] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cardsContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll logic
  useEffect(() => {
    if (isProcessing) {
        if (viewMode === 'batch' && textareaRef.current) {
            textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        } else if (viewMode === 'individual' && cardsContainerRef.current) {
            cardsContainerRef.current.scrollTop = cardsContainerRef.current.scrollHeight;
        }
    }
  }, [results, isProcessing, viewMode]);

  // Combine results for batch view
  const batchText = results.map(r => r.content).join('\n');

  const handleCopyText = async (text: string, id: string | 'global') => {
    try {
      await navigator.clipboard.writeText(text);
      if (id === 'global') {
        setGlobalCopied(true);
        setTimeout(() => setGlobalCopied(false), 2000);
      } else {
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleDownload = () => {
    const element = document.createElement("a");
    const file = new Blob([batchText], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = "prompts.txt";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div className="w-full h-full flex flex-col bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden shadow-sm">
      {/* Header with Controls */}
      <div className="flex flex-col gap-3 p-4 border-b border-slate-700 bg-slate-800">
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-blue-400">
            <Sparkles size={18} />
            <h3 className="font-semibold text-white">
                Generated Prompts 
                {progress.total > 0 && <span className="ml-2 text-slate-400 text-sm font-normal">({progress.current}/{progress.total})</span>}
            </h3>
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center bg-slate-900 rounded-lg p-1 border border-slate-700">
                <button
                    onClick={() => setViewMode('batch')}
                    className={`p-1.5 rounded-md transition-all ${viewMode === 'batch' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                    title="Batch View (One prompt per line)"
                >
                    <LayoutList size={16} />
                </button>
                <button
                    onClick={() => setViewMode('individual')}
                    className={`p-1.5 rounded-md transition-all ${viewMode === 'individual' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                    title="Individual View (Cards)"
                >
                    <Layers size={16} />
                </button>
            </div>
        </div>

        {/* Global Actions */}
        <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">
                {results.length} prompt{results.length !== 1 ? 's' : ''} generated
            </span>
            <div className="flex gap-2">
                <button
                    onClick={handleDownload}
                    disabled={results.length === 0}
                    className="p-1.5 px-3 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    title="Download all as .txt"
                >
                    <Download size={14} />
                    Download All
                </button>
                <button
                    onClick={() => handleCopyText(batchText, 'global')}
                    disabled={results.length === 0}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                    globalCopied 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-blue-600 text-white hover:bg-blue-500 shadow-lg shadow-blue-500/20'
                    } disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none`}
                >
                    {globalCopied ? (
                    <>
                        <Check size={14} />
                        <span>Copied All</span>
                    </>
                    ) : (
                    <>
                        <Copy size={14} />
                        <span>Copy All</span>
                    </>
                    )}
                </button>
            </div>
        </div>
      </div>
      
      {/* Content Area */}
      <div className="flex-1 relative bg-slate-900/50">
        {viewMode === 'batch' ? (
             <textarea
                ref={textareaRef}
                readOnly
                value={batchText}
                className="w-full h-full p-4 bg-transparent text-slate-300 font-mono text-xs md:text-sm resize-none focus:outline-none leading-relaxed"
                placeholder="Prompts will appear here line by line as they are generated..."
                spellCheck={false}
           />
        ) : (
            <div ref={cardsContainerRef} className="absolute inset-0 overflow-y-auto p-4 space-y-4">
                {results.length === 0 && (
                     <div className="h-full flex flex-col items-center justify-center text-slate-500 gap-2 opacity-50">
                        <Layers size={32} />
                        <span className="text-sm">Waiting for results...</span>
                     </div>
                )}
                {results.map((result, index) => (
                    <div key={index} className="bg-[#141517] border border-white/5 rounded-xl overflow-hidden shadow-lg transition-all hover:border-white/10 group">
                        {/* Card Header */}
                        <div className="px-4 py-2 bg-white/5 border-b border-white/5 flex items-center justify-between">
                             <div className="flex items-center gap-2 overflow-hidden">
                                <FileText size={14} className="text-blue-400 shrink-0" />
                                <span className="text-xs text-slate-300 truncate font-medium max-w-[200px]" title={result.filename}>
                                    {result.filename}
                                </span>
                             </div>
                             <div className="flex items-center gap-2">
                                <span className="text-[10px] text-slate-600 font-mono">{result.content.length} chars</span>
                                <button
                                    onClick={() => handleCopyText(result.content, `card-${index}`)}
                                    className={`p-1.5 rounded-md transition-colors ${
                                        copiedId === `card-${index}` 
                                            ? 'bg-green-500/20 text-green-400' 
                                            : 'text-slate-400 hover:text-white hover:bg-slate-700'
                                    }`}
                                    title="Copy this prompt"
                                >
                                    {copiedId === `card-${index}` ? <Check size={14} /> : <Copy size={14} />}
                                </button>
                             </div>
                        </div>
                        {/* Card Body */}
                        <div className="relative">
                            <textarea 
                                readOnly
                                value={result.content}
                                className={`w-full h-32 p-3 bg-transparent text-xs font-mono resize-y focus:outline-none leading-relaxed ${result.isError ? 'text-red-400' : 'text-slate-400'}`}
                                spellCheck={false}
                            />
                        </div>
                    </div>
                ))}
            </div>
        )}
      </div>

      <div className="p-3 bg-slate-900/50 border-t border-slate-800 flex justify-between items-center text-[10px] text-slate-500">
        <span>{viewMode === 'batch' ? 'Batch Mode (CSV Style)' : 'Individual Mode (Cards)'}</span>
        <span>Google Gemini API</span>
      </div>
    </div>
  );
};